import type { CategoryModelFragment } from '@hub/generated/graphql';
import type { AssociationType } from '@hub/bearing-hub/ProductDetails/SuitableProducts/associations';
import type {
  AssociationIbhProductFragmentEnhanced,
  AssociationProductFragmentEnhanced,
  ProductFragmentEnhanced,
  ProductToProductAssociation,
} from '@hub/models/enhanced-fragments';
import { GraphQlPriceType } from '@hub/models/graphql';
import type { ParsedUrlQuery } from 'querystring';
import {
  CATEGORIES_FOR_UNIT_SYSTEM_METRIC,
  IMPERIAL_UNIT_SYSTEM_LOCALES,
  LOCALES_TO_USE_US_PRODUCT_ID,
} from './constants';
import { PriceQuantityUnit } from './product-price-utils';
import { UnitSystem } from '@igus/kopla-domain';
import { CATEGORY_BEARINGS_IBH, CategoryHelper } from './category.helper';

export type CategoryWithParent = {
  code: string;
  category?: CategoryWithParent;
};

export const flattenCategoryTree = (category: CategoryWithParent): string[] => {
  if (category.category) {
    // if it has a parent flatten its parent
    return [...flattenCategoryTree(category.category), category.code];
  }
  // if it has no parent return its code
  return [category.code];
};

/**
 * returns the category keys as a flat array for a single product
 */
export const getCategoryKeysForProduct = (model: CategoryModelFragment) =>
  model.model_categories.flatMap(category => flattenCategoryTree(category.category as CategoryWithParent));

/**
 * Checks if the given model is enabled/disabled by feature flags. First checks feature flags on category level.
 * For some categories an additional check on the specific model is performed.
 * @param model The model to check
 */
export const isEnabledByFeatureFlag = (
  model: CategoryModelFragment,
  includeIbh = false,
  modelCategories?: string[]
) => {
  const categoryKeys = modelCategories || getCategoryKeysForProduct(model);
  if (includeIbh && categoryKeys.some(key => CATEGORY_BEARINGS_IBH.includes(key))) {
    return true;
  }
  return CategoryHelper.isEnabled(categoryKeys);
};

/**
 * returns the associated Products: suitable Products or Alternative Products if set for given product
 */
export const getAssociationProductReferences = (
  product: ProductFragmentEnhanced,
  associationType: (
    | AssociationType.ALTERNATIVE_PRODUCT
    | AssociationType.SUITABLE_PRODUCT
    | AssociationType.SEPARATORS
    | AssociationType.SHELVES
  )[],
  filter: (associationFilter: ProductToProductAssociation) => boolean = () => true
) => {
  const associatedProductReferences = product.product_to_product_associations
    ?.filter(association => associationType.some(type => association.type === type) && filter(association))
    ?.map(associatedProduct => associatedProduct.productByProductIdTarget)
    ?.filter(productByProductIdTarget => Boolean(productByProductIdTarget));
  if (!associatedProductReferences) {
    return null;
  }
  return associatedProductReferences;
};

export const removeUnnecessaryAttributes = (
  product: ProductFragmentEnhanced | AssociationProductFragmentEnhanced | AssociationIbhProductFragmentEnhanced,
  requiredAttributeNames: string[]
): ProductFragmentEnhanced | AssociationProductFragmentEnhanced | AssociationIbhProductFragmentEnhanced => {
  if (!product.attributes) {
    return product;
  }

  const availableAttributeNames = Object.keys(product.attributes);
  availableAttributeNames.forEach(attributeName => {
    if (!requiredAttributeNames.includes(attributeName)) {
      // eslint-disable-next-line no-param-reassign
      delete product.attributes?.[attributeName];
    }
  });

  return product;
};

// TODO: remove this function when the deprecated priceType is removed from the DB
export const removeDeprecatedAssociationProductPriceType = (
  associationProduct: AssociationProductFragmentEnhanced
): AssociationProductFragmentEnhanced => {
  if (!associationProduct.aggregated_product_prices) {
    return associationProduct;
  }

  associationProduct.aggregated_product_prices.forEach(aggregatedProductPrice => {
    if ((aggregatedProductPrice.prices as Record<string, unknown>).priceType) {
      // eslint-disable-next-line no-param-reassign
      delete (aggregatedProductPrice.prices as Record<string, unknown>).priceType;
    }
  });

  return associationProduct;
};

/**
 * @param query
 * @returns the value of the artnr query param while disregarding case sensitivity
 */
export const getQueryArtNr = (query: ParsedUrlQuery) => {
  // case-insensitive artnr
  const artnrRegex = /artnr/gi;

  const artnrQueryEntry = Object.entries(query).find(([key]) => artnrRegex.test(key));

  // should be a key/value tuple returned by object.entries ([key])
  return artnrQueryEntry?.length !== 2 ? (query.artnr as string) : (artnrQueryEntry[1] as string);
};

export const getProductIdForLocale = (
  product: Pick<ProductFragmentEnhanced, 'id' | 'id_us'>,
  locale: string
): string => (product.id_us && LOCALES_TO_USE_US_PRODUCT_ID.includes(locale) ? product.id_us : product.id);

export const determineProductUnitSystem = (locale: string, primaryCategoryCode: string): UnitSystem => {
  if (CATEGORIES_FOR_UNIT_SYSTEM_METRIC.includes(primaryCategoryCode)) {
    return UnitSystem.metric;
  }

  return IMPERIAL_UNIT_SYSTEM_LOCALES.includes(locale) ? UnitSystem.imperial : UnitSystem.metric;
};

/**
 *
 * @deprecated use getPriceQuantityUnitLabel instead,
 * in case the priceQuantityUnit from the database is available.
 * The priceQuantityUnit is currently missing in the database table "aggregated_product_prices"
 * and only available in the "prices" table.
 */
export const getPriceQuantityUnitLabelDeprecated = (
  locale: string,
  priceType: GraphQlPriceType,
  primarilyProductCategory: string
): string => {
  const unitSystem = determineProductUnitSystem(locale, primarilyProductCategory);

  // rewrite the priceTypeToUnitDefaultMapping const in a switch case
  switch (priceType) {
    case GraphQlPriceType.ChainLink:
      return 'PRODUCT_OVERVIEW.PRICE_BOX.CHAINLINK.UNIT_SINGULAR';
    case GraphQlPriceType.ChainLength:
    case GraphQlPriceType.Length:
    case GraphQlPriceType.LeadScrewLength:
    case GraphQlPriceType.LengthWithoutCopper:
    case GraphQlPriceType.LengthWithCuttingCostWithoutCopper:
    case GraphQlPriceType.TribotapeLength:
      return unitSystem === UnitSystem.metric ? PriceQuantityUnit.METER : PriceQuantityUnit.FEET;
    case GraphQlPriceType.LengthAndPiece:
    case GraphQlPriceType.Piece:
    case GraphQlPriceType.PieceWithCopper:
    case GraphQlPriceType.PieceWithChainLengthDependency:
      return 'PRODUCT_OVERVIEW.PRICE_BOX.LENGTH_AND_PIECE.PIECE_ABBREVIATION';
    case GraphQlPriceType.LengthAndPieceWithoutCopper:
      return PriceQuantityUnit.MILLIMETER;
    default:
      return priceType;
  }
};
